import { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useToggle from "../../hooks/useToggle";
import { useTranslations } from "../../hooks/useTranslations";
import { useMe, useMembers } from "../../http/serviceportalApi";
import SVGAccountTie from "../../icons/AccountTie.svg?react";
import SVGAdminPanelSettings from "../../icons/AdminPanelSettings.svg?react";
import SVGMoreVert from "../../icons/MoreVert.svg?react";
import SVGOpenInNew from "../../icons/OpenInNew.svg?react";
import SVGQuickReferenceAll from "../../icons/QuickReferenceAll.svg?react";
import SVGWeekend from "../../icons/Weekend.svg?react";
import {
  checkHasAdminPermission,
  checkHasEconomicAdvisorPermission,
  checkHasPrivateHouseholdPermission,
  checkHasRevisorPermission,
  isRouteEconomicAdvisors,
  isRoutePrivateHousehold,
  isRouteRevisor,
} from "../../utils";
import styles from "./AppMenu.module.css";
import { IconButton } from "./Button";
import Icon from "./Icon";

const AppMenu = () => {
  const menu = useToggle();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslations();

  const { data: me } = useMe();
  const userPermissions = me?.permissions ?? [];

  const { data: members } = useMembers({
    page: 1,
    page_size: 1,
  });
  const hasMemberPermission = members?.items.length ?? 0 > 0;

  const isRouteAdmin = location.pathname.startsWith("/admin");

  const canNavigateToCustomerApp =
    hasMemberPermission &&
    (isRouteAdmin ||
      isRouteEconomicAdvisors(location) ||
      isRoutePrivateHousehold(location) ||
      isRouteRevisor(location));
  const canNavigateToAdminApp =
    checkHasAdminPermission(userPermissions) && !isRouteAdmin;
  const canNavigateToEconomicAdvisorApp =
    checkHasEconomicAdvisorPermission(userPermissions) &&
    !isRouteEconomicAdvisors(location);
  const canNavigateToPrivateHouseholdApp =
    checkHasPrivateHouseholdPermission(userPermissions) &&
    !isRoutePrivateHousehold(location);
  const canNavigateToRevisorApp =
    checkHasRevisorPermission(userPermissions) && !isRouteRevisor(location);

  useOnClickOutside(ref, menu.close);

  if (
    !canNavigateToCustomerApp &&
    !canNavigateToAdminApp &&
    !canNavigateToEconomicAdvisorApp &&
    !canNavigateToPrivateHouseholdApp
  ) {
    return null;
  }

  return (
    <div ref={ref} className={styles.appMenu}>
      <IconButton glyph={SVGMoreVert} buttonProps={{ onClick: menu.toggle }} />
      {menu.isOpen && (
        <ul className={styles.menu}>
          {canNavigateToCustomerApp && (
            <li>
              <Link
                to="/"
                target="_blank"
                className={styles.menuItem}
                onClick={menu.close}
              >
                <Icon glyph={SVGAdminPanelSettings} className={styles.icon} />
                {i18n.menu.clientPortal}
                <Icon glyph={SVGOpenInNew} className={styles.openInNew} />
              </Link>
            </li>
          )}
          {canNavigateToAdminApp && (
            <li>
              <Link
                to="/admin"
                target="_blank"
                className={styles.menuItem}
                onClick={menu.close}
              >
                <Icon glyph={SVGAdminPanelSettings} className={styles.icon} />
                {i18n.menu.admin}
                <Icon glyph={SVGOpenInNew} className={styles.openInNew} />
              </Link>
            </li>
          )}
          {canNavigateToEconomicAdvisorApp && (
            <li>
              <Link
                to="/wirtschaftsberater"
                target="_blank"
                className={styles.menuItem}
                onClick={menu.close}
              >
                <Icon glyph={SVGAccountTie} className={styles.icon} />
                {i18n.menu.economicPortal}
                <Icon glyph={SVGOpenInNew} className={styles.openInNew} />
              </Link>
            </li>
          )}
          {canNavigateToPrivateHouseholdApp && (
            <li>
              <Link
                to="/privathaushalt"
                target="_blank"
                className={styles.menuItem}
                onClick={menu.close}
              >
                <Icon glyph={SVGWeekend} className={styles.icon} />
                {i18n.menu.privatePortal}
                <Icon glyph={SVGOpenInNew} className={styles.openInNew} />
              </Link>
            </li>
          )}
          {canNavigateToRevisorApp && (
            <li>
              <Link
                to="/revisoren"
                target="_blank"
                className={styles.menuItem}
                onClick={menu.close}
              >
                <Icon glyph={SVGQuickReferenceAll} className={styles.icon} />
                {i18n.menu.auditPortal}
                <Icon glyph={SVGOpenInNew} className={styles.openInNew} />
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default AppMenu;
